import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { formatXof } from '../utils/format';
import { useAuth } from '../context/AuthContext';

const EVAL_STATUS = {
  BROUILLON: ['gray', 'Brouillon'], VALIDE: ['blue', 'Validée'],
  PUBLIE: ['green', 'Publiée'], CORRIGE: ['blue', 'Corrigée'], CLOTURE: ['gold', 'Clôturée'],
};

function ParentDashboard() {
  const navigate = useNavigate();
  const [children, setChildren] = useState([]);
  const [annonces, setAnnonces] = useState([]);
  const [declarations, setDeclarations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // 'absence' | 'reclamation' | null
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const today = new Date().toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  useEffect(() => {
    Promise.all([
      api.get('/parents/my-children').catch(() => []),
      api.get('/registry/annonces').catch(() => ({ records: [] })),
      api.get('/parents/declarations-absence').catch(() => []),
    ]).then(([ch, ann, decl]) => {
      setChildren(Array.isArray(ch) ? ch : []);
      const all = (Array.isArray(ann) ? ann : (ann?.records || [])).map((r) => ({ id: r.id, ...r.data }));
      setAnnonces(all.filter((a) => a.status === 'Publiée').sort((a, b) => (b.publishedAt || '').localeCompare(a.publishedAt || '')).slice(0, 4));
      setDeclarations(Array.isArray(decl) ? decl : []);
    }).finally(() => setLoading(false));
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const endpoint = modal === 'absence' ? '/parents/declarations-absence' : '/parents/reclamations';
      await api.post(endpoint, { data: form, label: modal === 'absence' ? 'Déclaration absence' : 'Réclamation', searchText: '' });
      setModal(null);
      setForm({});
      if (modal === 'absence') {
        api.get('/parents/declarations-absence').catch(() => []).then((d) => setDeclarations(Array.isArray(d) ? d : []));
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) return <div className="loading">Chargement…</div>;

  return (
    <>
      <div style={{ marginBottom: 8, color: 'var(--muted)', fontSize: '0.9rem', textTransform: 'capitalize' }}>{today}</div>

      {/* Mes enfants */}
      <div className="panel" style={{ marginBottom: 20 }}>
        <div className="panel-head"><h2>👨‍👩‍👧 Mes enfants</h2></div>
        <div className="panel-body">
          {children.length === 0 ? (
            <div className="empty">Aucun enfant rattaché à ce compte.</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
              {children.map((sp) => {
                const s = sp.student;
                const enr = (s?.enrollments || [])[0];
                return (
                  <div key={sp.id} style={{ padding: '16px 18px', border: '1px solid var(--line)', borderRadius: 12, background: 'var(--surface-soft)' }}>
                    <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: 4 }}>{s?.firstName} {s?.lastName}</div>
                    {enr && (
                      <>
                        <div style={{ fontSize: 13, color: 'var(--muted)' }}>Classe : <b>{enr.class?.name || '—'}</b></div>
                        <div style={{ fontSize: 13, color: 'var(--muted)' }}>Année : {enr.academicYear?.label || '—'}</div>
                      </>
                    )}
                    <div style={{ marginTop: 8 }}>
                      <span className={`badge ${enr ? 'green' : 'gray'}`}>{enr ? 'Inscrit' : 'Non inscrit'}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Actions rapides */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
        <button onClick={() => { setModal('absence'); setForm({}); }}
          style={{ padding: '18px 14px', borderRadius: 12, border: '1px solid #bfdbfe', background: '#eff6ff', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <span style={{ fontSize: '2rem' }}>📋</span>
          <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1d4ed8' }}>Déclarer une absence</span>
        </button>
        <button onClick={() => { setModal('reclamation'); setForm({}); }}
          style={{ padding: '18px 14px', borderRadius: 12, border: '1px solid #ddd6fe', background: '#f5f3ff', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <span style={{ fontSize: '2rem' }}>✉️</span>
          <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#7c3aed' }}>Soumettre une réclamation</span>
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* Annonces */}
        <div className="panel">
          <div className="panel-head">
            <h2>Annonces de l'école</h2>
            <button className="btn-ghost btn-sm" onClick={() => navigate('/communication/annonces')}>Voir tout →</button>
          </div>
          <div className="panel-body">
            {annonces.length === 0 ? <div className="empty">Aucune annonce.</div> : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {annonces.map((a) => (
                  <div key={a.id} style={{ padding: '10px 12px', border: '0.5px solid var(--line)', borderRadius: 8 }}>
                    <div style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
                      <span className={`badge ${a.type === 'Résultat' ? 'green' : a.type === 'Événement' ? 'navy' : 'gold'}`} style={{ fontSize: 10 }}>📢 {a.type}</span>
                      <span style={{ fontSize: 11, color: 'var(--muted)', marginLeft: 'auto' }}>{a.publishedAt ? new Date(a.publishedAt).toLocaleDateString('fr-FR') : '—'}</span>
                    </div>
                    <div style={{ fontWeight: 500, fontSize: 13 }}>{a.title}</div>
                    <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 3 }}>{(a.content || '').slice(0, 80)}…</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Mes déclarations d'absence */}
        <div className="panel">
          <div className="panel-head"><h2>Mes déclarations d'absence</h2></div>
          <div className="panel-body">
            {declarations.length === 0 ? <div className="empty">Aucune déclaration.</div> : (
              <div className="table-wrap"><table>
                <thead><tr><th>Enfant</th><th>Motif</th><th>Date</th></tr></thead>
                <tbody>{declarations.slice(0, 6).map((d) => (
                  <tr key={d.id}>
                    <td>{d.studentName || '—'}</td>
                    <td>{d.motif || '—'}</td>
                    <td className="mono" style={{ fontSize: 12 }}>{d.date ? new Date(d.date).toLocaleDateString('fr-FR') : '—'}</td>
                  </tr>
                ))}</tbody>
              </table></div>
            )}
          </div>
        </div>
      </div>

      {/* Modal déclaration / réclamation */}
      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setModal(null)}>
          <div style={{ background: 'var(--surface)', borderRadius: 14, padding: 28, width: 420, boxShadow: '0 8px 32px rgba(0,0,0,.18)' }}
            onClick={(e) => e.stopPropagation()}>
            <h2 style={{ marginBottom: 18, fontSize: '1.05rem' }}>
              {modal === 'absence' ? '📋 Déclarer une absence' : '✉️ Soumettre une réclamation'}
            </h2>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {modal === 'absence' && (
                <>
                  <div>
                    <label style={{ fontSize: 13, fontWeight: 500 }}>Enfant concerné</label>
                    <select required style={{ width: '100%', marginTop: 4 }} value={form.studentName || ''} onChange={(e) => setForm({ ...form, studentName: e.target.value })}>
                      <option value="">— Sélectionner —</option>
                      {children.map((sp) => <option key={sp.id} value={`${sp.student?.firstName} ${sp.student?.lastName}`}>{sp.student?.firstName} {sp.student?.lastName}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: 13, fontWeight: 500 }}>Date d'absence</label>
                    <input type="date" required style={{ width: '100%', marginTop: 4 }} value={form.date || ''} onChange={(e) => setForm({ ...form, date: e.target.value })} />
                  </div>
                  <div>
                    <label style={{ fontSize: 13, fontWeight: 500 }}>Motif</label>
                    <input type="text" required placeholder="Maladie, rendez-vous médical…" style={{ width: '100%', marginTop: 4 }} value={form.motif || ''} onChange={(e) => setForm({ ...form, motif: e.target.value })} />
                  </div>
                </>
              )}
              {modal === 'reclamation' && (
                <>
                  <div>
                    <label style={{ fontSize: 13, fontWeight: 500 }}>Objet</label>
                    <input type="text" required placeholder="Objet de la réclamation" style={{ width: '100%', marginTop: 4 }} value={form.objet || ''} onChange={(e) => setForm({ ...form, objet: e.target.value })} />
                  </div>
                  <div>
                    <label style={{ fontSize: 13, fontWeight: 500 }}>Message</label>
                    <textarea required rows={4} placeholder="Décrivez votre réclamation…" style={{ width: '100%', marginTop: 4 }} value={form.message || ''} onChange={(e) => setForm({ ...form, message: e.target.value })} />
                  </div>
                </>
              )}
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
                <button type="button" className="btn-ghost" onClick={() => setModal(null)}>Annuler</button>
                <button type="submit" className="btn" disabled={submitting}>{submitting ? 'Envoi…' : 'Envoyer'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

function EnseignantDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [coursJour, setCoursJour] = useState([]);
  const [evals, setEvals] = useState([]);
  const [annonces, setAnnonces] = useState([]);
  const [loading, setLoading] = useState(true);
  const today = new Date().toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  useEffect(() => {
    const userId = user?.id;
    // dayOfWeek : JS getDay() 0=dim..6=sam → ISO 1=lun..7=dim
    const jsDay = new Date().getDay();
    const dayOfWeek = jsDay === 0 ? 7 : jsDay;
    Promise.all([
      userId ? api.get(`/timetable?teacherId=${userId}`).catch(() => []) : Promise.resolve([]),
      api.get('/evaluations').catch(() => []),
      api.get('/registry/annonces').catch(() => ({ records: [] })),
    ]).then(([cours, evs, ann]) => {
      // Filtrer les cours de cet enseignant pour aujourd'hui
      const mesCours = (Array.isArray(cours) ? cours : []).filter((c) => c.dayOfWeek === dayOfWeek);
      setCoursJour(mesCours);
      // Filtrer les évaluations de cet enseignant
      const mesEvals = (Array.isArray(evs) ? evs : []).filter(
        (e) => !userId || e.teacherId === userId || e.teacher?.id === userId
      );
      setEvals(mesEvals);
      const all = (Array.isArray(ann) ? ann : (ann?.records || [])).map((r) => ({ id: r.id, ...r.data }));
      setAnnonces(all.filter((a) => a.status === 'Publiée').sort((a, b) => (b.publishedAt || '').localeCompare(a.publishedAt || '')).slice(0, 4));
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading">Chargement…</div>;

  const SHORTCUTS = [
    { label: 'Saisir présences', icon: '✅', path: '/presences',      bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0' },
    { label: 'Mes évaluations',  icon: '📝', path: '/evaluations',    bg: '#fffbeb', color: '#d97706', border: '#fde68a' },
    { label: 'Emploi du temps',  icon: '📅', path: '/emploi-du-temps',bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
    { label: 'Annonces',         icon: '📢', path: '/communication/annonces', bg: '#f5f3ff', color: '#7c3aed', border: '#ddd6fe' },
  ];

  return (
    <>
      <div style={{ marginBottom: 8, color: 'var(--muted)', fontSize: '0.9rem', textTransform: 'capitalize' }}>{today}</div>

      {/* KPIs enseignant */}
      <div className="kpi-grid" style={{ marginBottom: 16 }}>
        <div className="kpi gold">
          <span>Mes évaluations</span><strong>{evals.length}</strong>
        </div>
        <div className="kpi navy">
          <span>Cours aujourd'hui</span><strong>{coursJour.length}</strong>
        </div>
        <div className="kpi green">
          <span>Évaluations publiées</span>
          <strong>{evals.filter((e) => e.status === 'PUBLIE').length}</strong>
        </div>
        <div className="kpi">
          <span>En brouillon</span>
          <strong>{evals.filter((e) => e.status === 'BROUILLON').length}</strong>
        </div>
      </div>

      {/* Raccourcis */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
        {SHORTCUTS.map((s) => (
          <button key={s.label} onClick={() => navigate(s.path)}
            style={{ padding: '18px 14px', borderRadius: 12, textAlign: 'center', border: `1px solid ${s.border}`, background: s.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,.06)' }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,.10)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,.06)'; }}>
            <span style={{ fontSize: '2rem', lineHeight: 1 }}>{s.icon}</span>
            <span style={{ fontSize: '0.82rem', fontWeight: 600, color: s.color }}>{s.label}</span>
          </button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        {/* Cours du jour */}
        <div className="panel">
          <div className="panel-head">
            <h2>● Mes cours du jour ({new Date().toLocaleDateString('fr-FR', { weekday: 'long' })})</h2>
            <button className="btn-ghost btn-sm" onClick={() => navigate('/emploi-du-temps')}>Emploi du temps →</button>
          </div>
          <div className="panel-body">
            {coursJour.length === 0 ? <div className="empty">Aucun cours aujourd'hui.</div> : (
              <div className="table-wrap"><table>
                <thead><tr><th>Heure</th><th>Classe</th><th>Matière</th><th>Salle</th></tr></thead>
                <tbody>{coursJour.map((c) => (
                  <tr key={c.id}>
                    <td className="mono" style={{ fontSize: 12 }}>{c.startTime} – {c.endTime}</td>
                    <td><b>{c.class?.name || '—'}</b></td>
                    <td>{c.subject?.name || '—'}</td>
                    <td>{c.room?.name ? <span className="badge gray">{c.room.name}</span> : '—'}</td>
                  </tr>
                ))}</tbody>
              </table></div>
            )}
          </div>
        </div>

        {/* Annonces */}
        <div className="panel">
          <div className="panel-head">
            <h2>Annonces de l'école</h2>
            <button className="btn-ghost btn-sm" onClick={() => navigate('/communication/annonces')}>Voir tout →</button>
          </div>
          <div className="panel-body">
            {annonces.length === 0 ? <div className="empty">Aucune annonce.</div> : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {annonces.map((a) => (
                  <div key={a.id} style={{ padding: '10px 12px', border: '0.5px solid var(--line)', borderRadius: 8 }}>
                    <div style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
                      <span className={`badge ${a.type === 'Résultat' ? 'green' : a.type === 'Événement' ? 'navy' : 'gold'}`} style={{ fontSize: 10 }}>📢 {a.type}</span>
                      <span style={{ fontSize: 11, color: 'var(--muted)', marginLeft: 'auto' }}>{a.publishedAt ? new Date(a.publishedAt).toLocaleDateString('fr-FR') : '—'}</span>
                    </div>
                    <div style={{ fontWeight: 500, fontSize: 13 }}>{a.title}</div>
                    <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 3 }}>{(a.content || '').slice(0, 80)}…</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mes évaluations récentes */}
      <div className="panel">
        <div className="panel-head">
          <h2>Mes évaluations récentes</h2>
          <button className="btn-ghost btn-sm" onClick={() => navigate('/evaluations')}>Voir tout →</button>
        </div>
        <div className="panel-body">
          {evals.length === 0 ? <div className="empty">Aucune évaluation.</div> : (
            <div className="table-wrap"><table>
              <thead><tr><th>Date</th><th>Titre</th><th>Classe</th><th>Matière</th><th>Statut</th></tr></thead>
              <tbody>{evals.slice(0, 8).map((e) => {
                const [cls, label] = EVAL_STATUS[e.status] || ['gray', e.status];
                return (
                  <tr key={e.id} className="clickable" onClick={() => navigate(`/evaluations/${e.id}`)}>
                    <td className="mono" style={{ fontSize: 12 }}>{new Date(e.date).toLocaleDateString('fr-FR')}</td>
                    <td>{e.title}</td>
                    <td>{e.class?.name || '—'}</td>
                    <td>{e.subject?.name || '—'}</td>
                    <td><span className={`badge ${cls}`}>{label}</span></td>
                  </tr>
                );
              })}</tbody>
            </table></div>
          )}
        </div>
      </div>
    </>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, perms } = useAuth();
  const hasModule = (mod) => !perms || perms[mod] !== false;

  const _roles = user?.roles || [];
  const _STAFF = ['ADMINISTRATEUR','PROVISEUR','CENSEUR','SECRETAIRE','COMPTABLE','SURVEILLANT'];
  const isPureParent     = _roles.includes('PARENT') && !_roles.some(r => _STAFF.includes(r)) && !_roles.includes('ENSEIGNANT');
  const isPureEnseignant = _roles.includes('ENSEIGNANT') && !_roles.some(r => _STAFF.includes(r));

  // Tous les hooks AVANT tout return conditionnel (Rules of Hooks)
  const [data, setData] = useState(null);
  const [annonces, setAnnonces] = useState([]);
  const [coursJour, setCoursJour] = useState([]);
  const [tresorerie, setTresorerie] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    // Ne pas charger les données admin si c'est un parent ou enseignant pur
    if (isPureParent) return;
    if (isPureEnseignant) return;

    // Annonces récentes
    api.get('/registry/annonces').then((d) => {
      const all = (Array.isArray(d) ? d : (d?.records || [])).map((r) => ({ id: r.id, ...r.data }));
      setAnnonces(all.filter((a) => a.status === 'Publiée').sort((a, b) => (b.publishedAt || '').localeCompare(a.publishedAt || '')).slice(0, 3));
    }).catch(() => {});
    // Cours du jour
    const todayIso = new Date().toISOString().slice(0, 10);
    api.get(`/timetable?date=${todayIso}`).then(setCoursJour).catch(() => {});
    // Résumé trésorerie
    api.get('/finance/summary').then(setTresorerie).catch(() => {});

    Promise.all([
      api.get('/students').catch(() => []),
      api.get('/evaluations').catch(() => []),
      api.get('/staff').catch(() => []),
      api.get('/library/loans').catch(() => []),
      api.get('/library/overdue').catch(() => []),
      api.get('/hr/contracts?status=ACTIVE').catch(() => []),
      api.get('/finance/unpaid').catch(() => []),
    ]).then(([students, evals, staff, loans, overdue, contracts, unpaid]) => {
      setData({ students, evals, staff, loans, overdue, contracts, unpaid });
    }).catch((e) => setError(e.message)).finally(() => setLoading(false));
  }, []);

  if (isPureParent) return <ParentDashboard />;
  if (isPureEnseignant) return <EnseignantDashboard />;

  const today = new Date().toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  if (loading) return <div className="loading">Chargement du tableau de bord…</div>;
  if (error) return <div className="form-error">{error}</div>;
  if (!data) return null;

  const { students, evals, staff, loans, overdue, contracts, unpaid } = data;
  const classes = new Set(students.flatMap((s) => s.enrollments?.map((e) => e.classId) || [])).size;
  const totalImpaye = (unpaid || []).reduce((s, r) => s + Number(r.balance || r.solde || 0), 0);
  const activeLoans = (loans || []).filter((l) => (l.status || 'EN_COURS') === 'EN_COURS').length;

  const tauxRecouvrement = tresorerie && tresorerie.totalFacture > 0
    ? Math.round((tresorerie.totalPaye / tresorerie.totalFacture) * 100)
    : 0;

  const ALL_SHORTCUTS = [
    { label: 'Élèves',       icon: '👨‍🎓', path: '/eleves',                  module: 'students',      bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
    { label: 'Personnel',    icon: '👨‍🏫', path: '/communication/personnel', module: 'staff',         bg: '#f5f3ff', color: '#7c3aed', border: '#ddd6fe' },
    { label: 'Présences',    icon: '✅',   path: '/presences',               module: 'attendance',    bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0' },
    { label: 'Évaluations',  icon: '📝',  path: '/evaluations',             module: 'evaluations',   bg: '#fffbeb', color: '#d97706', border: '#fde68a' },
    { label: 'Finance',      icon: '💳',  path: '/finance',                 module: 'finance',       bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0' },
    { label: 'Bibliothèque', icon: '📚',  path: '/bibliotheque/emprunts',   module: 'library',       bg: '#fff7ed', color: '#c2410c', border: '#fed7aa' },
    { label: 'Contrats',     icon: '📋',  path: '/communication/contrats',  module: 'hr',            bg: '#fdf4ff', color: '#9333ea', border: '#e9d5ff' },
    { label: 'Santé',        icon: '🏥',  path: '/support/sante',           module: null,            bg: '#fff1f2', color: '#e11d48', border: '#fecdd3' },
  ];
  const SHORTCUTS = ALL_SHORTCUTS.filter((s) => !s.module || hasModule(s.module));

  return (
    <>
      <div style={{ marginBottom: 8, color: 'var(--muted)', fontSize: '0.9rem', textTransform: 'capitalize' }}>{today}</div>

      {/* KPIs principaux */}
      <div className="kpi-grid">
        {hasModule('students') && (
          <div className="kpi navy" style={{ cursor: 'pointer' }} onClick={() => navigate('/students')}>
            <span>Élèves inscrits</span><strong>{students.length}</strong>
          </div>
        )}
        {hasModule('staff') && (
          <div className="kpi" style={{ cursor: 'pointer' }} onClick={() => navigate('/communication/personnel')}>
            <span>Personnel</span><strong>{staff.length}</strong>
          </div>
        )}
        {hasModule('structure') && (
          <div className="kpi gold">
            <span>Classes actives</span><strong>{classes}</strong>
          </div>
        )}
        {hasModule('hr') && (
          <div className="kpi green" style={{ cursor: 'pointer' }} onClick={() => navigate('/communication/contrats')}>
            <span>Contrats actifs</span><strong>{(contracts || []).length}</strong>
          </div>
        )}
      </div>

      <div className="kpi-grid">
        {hasModule('finance') && (
          <div className="kpi" style={{ cursor: 'pointer' }} onClick={() => navigate('/finance')}>
            <span>Impayés total</span>
            <strong style={{ color: totalImpaye > 0 ? 'var(--danger)' : 'inherit' }}>{formatXof(totalImpaye)}</strong>
          </div>
        )}
        {hasModule('library') && (
          <div className="kpi" style={{ cursor: 'pointer' }} onClick={() => navigate('/bibliotheque/emprunts')}>
            <span>Emprunts en cours</span><strong>{activeLoans}</strong>
          </div>
        )}
        {hasModule('library') && (
          <div className="kpi" style={{ cursor: 'pointer', background: (overdue || []).length > 0 ? 'var(--danger-soft, #fff5f5)' : '' }} onClick={() => navigate('/bibliotheque/retards')}>
            <span>Retards bibliothèque</span>
            <strong style={{ color: (overdue || []).length > 0 ? 'var(--danger)' : 'inherit' }}>{(overdue || []).length}</strong>
          </div>
        )}
        {hasModule('evaluations') && (
          <div className="kpi gold">
            <span>Évaluations</span><strong>{evals.length}</strong>
          </div>
        )}
      </div>

      {/* Widget Trésorerie */}
      {hasModule('finance') && tresorerie && (
        <div className="panel" style={{ marginBottom: 20, cursor: 'pointer' }} onClick={() => navigate('/finance')}>
          <div className="panel-head">
            <h2>💰 Trésorerie — {new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}</h2>
            <button className="btn-ghost btn-sm" onClick={(e) => { e.stopPropagation(); navigate('/finance'); }}>Voir le détail →</button>
          </div>
          <div className="panel-body">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 14 }}>
              <div style={{ padding: '14px 16px', background: 'var(--surface-soft)', borderRadius: 10, borderLeft: '3px solid var(--success, #16a34a)' }}>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 4 }}>Recettes du mois</div>
                <div style={{ fontSize: '1.15rem', fontWeight: 700, fontFamily: 'monospace', color: 'var(--success, #16a34a)' }}>
                  {formatXof(tresorerie.recettesMois)}
                </div>
              </div>
              <div style={{ padding: '14px 16px', background: 'var(--surface-soft)', borderRadius: 10, borderLeft: '3px solid var(--primary, #3b82f6)' }}>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 4 }}>Total encaissé</div>
                <div style={{ fontSize: '1.15rem', fontWeight: 700, fontFamily: 'monospace', color: 'var(--primary, #3b82f6)' }}>
                  {formatXof(tresorerie.totalPaye)}
                </div>
              </div>
              <div style={{ padding: '14px 16px', background: 'var(--surface-soft)', borderRadius: 10, borderLeft: `3px solid ${tresorerie.solde > 0 ? 'var(--danger, #dc2626)' : 'var(--success, #16a34a)'}` }}>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 4 }}>Reste à encaisser</div>
                <div style={{ fontSize: '1.15rem', fontWeight: 700, fontFamily: 'monospace', color: tresorerie.solde > 0 ? 'var(--danger, #dc2626)' : 'var(--success, #16a34a)' }}>
                  {formatXof(tresorerie.solde)}
                </div>
              </div>
            </div>
            {/* Barre de recouvrement */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--muted)', marginBottom: 5 }}>
                <span>Taux de recouvrement</span>
                <span style={{ fontWeight: 600, color: tauxRecouvrement >= 80 ? 'var(--success, #16a34a)' : tauxRecouvrement >= 50 ? 'var(--warn, #d97706)' : 'var(--danger, #dc2626)' }}>
                  {tauxRecouvrement}%
                </span>
              </div>
              <div style={{ height: 6, background: 'var(--line)', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{
                  height: '100%',
                  width: `${Math.min(tauxRecouvrement, 100)}%`,
                  background: tauxRecouvrement >= 80 ? 'var(--success, #16a34a)' : tauxRecouvrement >= 50 ? 'var(--warn, #d97706)' : 'var(--danger, #dc2626)',
                  borderRadius: 4,
                  transition: 'width .4s ease',
                }} />
              </div>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
                {formatXof(tresorerie.totalPaye)} encaissés sur {formatXof(tresorerie.totalFacture)} facturés
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Raccourcis */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
        {SHORTCUTS.map((s) => (
          <button key={s.label} onClick={() => navigate(s.path)}
            style={{
              padding: '18px 14px', borderRadius: 12,
              textAlign: 'center',
              border: `1px solid ${s.border}`,
              background: s.bg,
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
              cursor: 'pointer', transition: 'transform .12s, box-shadow .12s',
              boxShadow: '0 1px 3px rgba(0,0,0,.06)',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,.10)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,.06)'; }}
          >
            <span style={{ fontSize: '2rem', lineHeight: 1 }}>{s.icon}</span>
            <span style={{ fontSize: '0.82rem', fontWeight: 600, color: s.color, letterSpacing: '.01em' }}>{s.label}</span>
          </button>
        ))}
      </div>

      {/* Widget cours du jour + Annonces */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        {hasModule('timetable') && (
          <div className="panel">
            <div className="panel-head">
              <h2>● Cours du jour ({new Date().toLocaleDateString('fr-FR', { weekday: 'long' })})</h2>
              <button className="btn-ghost btn-sm" onClick={() => navigate('/emploi-du-temps')}>Voir l'emploi du temps complet →</button>
            </div>
            <div className="panel-body">
              {coursJour.length === 0 ? <div className="empty">Aucun cours aujourd'hui.</div> : (
                <div className="table-wrap"><table>
                  <thead><tr><th>Heure</th><th>Classe</th><th>Matière</th><th>Enseignant</th><th>Salle</th></tr></thead>
                  <tbody>{coursJour.slice(0, 5).map((c) => (
                    <tr key={c.id}>
                      <td className="mono" style={{ fontSize: 12 }}>{c.startTime} – {c.endTime}</td>
                      <td><b>{c.class?.name || '—'}</b></td>
                      <td>{c.subject?.name || '—'}</td>
                      <td>{c.teacher?.lastName || '—'}</td>
                      <td>{c.room?.name ? <span className="badge gray">{c.room.name}</span> : '—'}</td>
                    </tr>
                  ))}</tbody>
                </table></div>
              )}
            </div>
          </div>
        )}
        {hasModule('communication') && (
          <div className="panel">
            <div className="panel-head">
              <h2>Dernières annonces</h2>
              <button className="btn-ghost btn-sm" onClick={() => navigate('/communication/annonces')}>Voir tout →</button>
            </div>
            <div className="panel-body">
              {annonces.length === 0 ? <div className="empty">Aucune annonce.</div> : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {annonces.map((a) => (
                    <div key={a.id} style={{ padding: '10px 12px', border: '0.5px solid var(--line)', borderRadius: 8 }}>
                      <div style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
                        <span className={`badge ${a.type === 'Résultat' ? 'green' : a.type === 'Événement' ? 'navy' : 'gold'}`} style={{ fontSize: 10 }}>📢 {a.type}</span>
                        <span style={{ fontSize: 11, color: 'var(--muted)', marginLeft: 'auto' }}>{a.publishedAt ? new Date(a.publishedAt).toLocaleDateString('fr-FR') : '—'}</span>
                      </div>
                      <div style={{ fontWeight: 500, fontSize: 13 }}>{a.title}</div>
                      <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 3 }}>{(a.content || '').slice(0, 80)}…</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* Évaluations récentes */}
        {hasModule('evaluations') && (
          <div className="panel">
            <div className="panel-head"><h2>Évaluations récentes</h2></div>
            <div className="panel-body">
              {evals.length === 0 ? <div className="empty">Aucune évaluation.</div> : (
                <div className="table-wrap"><table>
                  <thead><tr><th>Date</th><th>Titre</th><th>Classe</th><th>Statut</th></tr></thead>
                  <tbody>{evals.slice(0, 6).map((e) => {
                    const [cls, label] = EVAL_STATUS[e.status] || ['gray', e.status];
                    return (
                      <tr key={e.id} className="clickable" onClick={() => navigate(`/evaluations/${e.id}`)}>
                        <td className="mono">{new Date(e.date).toLocaleDateString('fr-FR')}</td>
                        <td>{e.title}</td>
                        <td>{e.class?.name || '—'}</td>
                        <td><span className={`badge ${cls}`}>{label}</span></td>
                      </tr>
                    );
                  })}</tbody>
                </table></div>
              )}
            </div>
          </div>
        )}

        {/* Retards bibliothèque */}
        {hasModule('library') && (
          <div className="panel">
            <div className="panel-head">
              <h2>Retards bibliothèque</h2>
              {(overdue || []).length > 0 && <span className="badge red">{(overdue || []).length}</span>}
            </div>
            <div className="panel-body">
              {(overdue || []).length === 0 ? <div className="empty">Aucun retard. ✓</div> : (
                <div className="table-wrap"><table>
                  <thead><tr><th>Élève</th><th>Ouvrage</th><th className="num">Retard</th></tr></thead>
                  <tbody>{(overdue || []).slice(0, 6).map((r) => (
                    <tr key={r.id}>
                      <td>{r.lastName || r.last_name} {r.firstName || r.first_name}</td>
                      <td>{r.title}</td>
                      <td className="num"><span className="badge red">{r.days_overdue}j</span></td>
                    </tr>
                  ))}</tbody>
                </table></div>
              )}
            </div>
          </div>
        )}

        {/* Impayés */}
        {hasModule('finance') && (
          <div className="panel">
            <div className="panel-head">
              <h2>Impayés récents</h2>
              {totalImpaye > 0 && <span className="badge red">{formatXof(totalImpaye)}</span>}
            </div>
            <div className="panel-body">
              {(unpaid || []).length === 0 ? <div className="empty">Aucun impayé. ✓</div> : (
                <div className="table-wrap"><table>
                  <thead><tr><th>Élève</th><th>Classe</th><th className="num">Solde</th></tr></thead>
                  <tbody>{(unpaid || []).slice(0, 6).map((r) => (
                    <tr key={r.studentId || r.student?.id} className="clickable" onClick={() => navigate('/finance')}>
                      <td>{r.lastName || r.student?.lastName} {r.firstName || r.student?.firstName}</td>
                      <td>{r.className || '—'}</td>
                      <td className="num" style={{ color: 'var(--danger)', fontWeight: 700 }}>{formatXof(r.balance || r.solde)}</td>
                    </tr>
                  ))}</tbody>
                </table></div>
              )}
            </div>
          </div>
        )}

        {/* Personnel actif */}
        {hasModule('staff') && (
          <div className="panel">
            <div className="panel-head"><h2>Personnel actif</h2></div>
            <div className="panel-body">
              {staff.length === 0 ? <div className="empty">Aucun agent.</div> : (
                <div className="table-wrap"><table>
                  <thead><tr><th>Nom</th><th>Type</th><th>Fonction</th></tr></thead>
                  <tbody>{staff.slice(0, 6).map((s) => (
                    <tr key={s.id} className="clickable" onClick={() => navigate('/communication/personnel')}>
                      <td><b>{s.lastName} {s.firstName}</b></td>
                      <td>{s.type === 'ENSEIGNANT' ? 'Enseignant' : s.type === 'SOUTIEN' ? 'Soutien' : 'Admin'}</td>
                      <td>{s.administrativeFunction || s.function || '—'}</td>
                    </tr>
                  ))}</tbody>
                </table></div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
